import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  Select,
  styled,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import Grid from "@mui/material/Grid2";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import StorageIcon from "@mui/icons-material/Storage";
import BatteryFullIcon from "@mui/icons-material/BatteryFull";
import NetworkCellIcon from "@mui/icons-material/NetworkCell";
import WifiIcon from "@mui/icons-material/Wifi";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CloseIcon from "@mui/icons-material/Close";
import DoneIcon from "@mui/icons-material/Done";
import VisibilityIcon from "@mui/icons-material/Visibility";
import * as XLSX from "xlsx";
import { useGetSearchFilesQuery } from "../store/services/principleApi";
import {
  useGetTemplatesQuery,
  useSendMessageBatchMutation,
  useGetPhoneNUmberQuery,
  useTestwhatsappmsgMutation,
  useGetwhatsappcampaignpreviewMutation, // NEW: add this mutation in whatsappApi
} from "../store/services/whatsappApi";
import { toast } from "react-toastify";
import { jwtDecode } from "jwt-decode";
import { useNavigate } from "react-router-dom";

// ── mpin status query ─────────────────────────────────────────────
import { useCheckMpinStatusQuery } from "../store/services/userApi";

// ── API base — used to resolve relative preview_url paths returned by our
//    own backend (e.g. "/templates/1/xxx/media/0").
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";

// --- Styled Components ---
const PhonePreview = styled(Card)(() => ({
  width: "240px",
  height: "460px",
  margin: "0 auto",
  border: "5px solid #111",
  borderRadius: "18px",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  background: "#fff",
  boxShadow: "0 12px 30px rgba(0,0,0,0.12)",
}));

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

// ── Shared field styling — always-visible border, hover only darkens it.
const sharedFieldRoot = {
  height: 35,
  borderRadius: "8px",
  background: "rgba(255,255,255,0.7)",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#CBD5E1",
    borderWidth: "1px",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#94A3B8",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#7c3aed",
    borderWidth: "2px",
  },
  "&.Mui-disabled .MuiOutlinedInput-notchedOutline": {
    borderColor: "#E2E8F0",
  },
};

const inputSx = {
  "& .MuiOutlinedInput-root": {
    ...sharedFieldRoot,
  },
  "& .MuiInputLabel-root": { color: "#475569" },
  "& .Mui-disabled": {
    "& .MuiOutlinedInput-input": {
      color: "#334155 !important",
      WebkitTextFillColor: "#334155 !important",
    },
    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#CBD5E1" },
  },
};

const selectSx = {
  "& .MuiOutlinedInput-root": {
    ...sharedFieldRoot,
    "& .MuiSelect-select": {
      padding: "0 14px",
      fontSize: "0.75rem",
      display: "flex",
      alignItems: "center",
      height: "100%",
      boxSizing: "border-box",
      minHeight: "unset",
    },
    "& .MuiSelect-icon": {
      right: 8,
      fontSize: "1.15rem",
    },
  },
  "& .Mui-disabled": {
    color: "#334155 !important",
    WebkitTextFillColor: "#334155 !important",
  },
  "&.Mui-disabled .MuiOutlinedInput-notchedOutline": { borderColor: "#CBD5E1" },
};

const compactField = {
  "& .MuiInputBase-root": { height: 34, fontSize: "0.75rem" },
  "& .MuiInputLabel-root": { fontSize: "0.7rem" },
};

const SectionTitle = ({ title }) => (
  <Typography sx={{ fontSize: "13px", fontWeight: 600, mb: 1, color: "#0F172A" }}>
    {title}
  </Typography>
);

// ── Grid of label/value cards used in the review modal ─────────────────────
const FieldGrid = ({ fields }) => (
  <Grid container spacing={1.5}>
    {fields.map(({ label, value }) => (
      <Grid key={label} size={4}>
        <Paper variant="outlined" sx={{ p: 1, borderRadius: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {label}
          </Typography>
          <Typography variant="subtitle2" fontWeight="bold">
            {value ?? "-"}
          </Typography>
        </Paper>
      </Grid>
    ))}
  </Grid>
);

// ── Campaign category options (value stored + sent to backend) ──────────────
const CAMPAIGN_CATEGORIES = [
  { value: 10, label: "Marketing" },
  { value: 20, label: "Sales" },
  { value: 30, label: "Authentication" },
];

// ── Friendly labels for template_type (non-auth only) ──────────────────────
const TEMPLATE_TYPE_DISPLAY = {
  TEXT: "Text",
  MEDIA: "Rich Card",
  INTERACTIVE: "Carousel",
};

const getTemplateTypeLabel = (templateType) =>
  TEMPLATE_TYPE_DISPLAY[templateType] || "";

// ─────────────────────────────────────────────────────────────────────────────
// Media URL resolution
// ─────────────────────────────────────────────────────────────────────────────
const resolvePreviewUrl = (path) => {
  if (!path) return "";
  if (/^https?:\/\//i.test(path) || path.startsWith("data:") || path.startsWith("blob:")) {
    return path;
  }
  return `${API_BASE_URL}${path}`;
};

const isUsableImageUrl = (value) =>
  typeof value === "string" && /^https?:\/\//i.test(value.trim());

const safeFallback = (headerHandle) =>
  isUsableImageUrl(headerHandle) ? headerHandle : "";

// Determine "image" | "video" | "unknown" from a media_files[] entry.
const mediaFileKind = (mediaFile) => {
  if (!mediaFile) return "unknown";
  if (mediaFile.category === "video" || mediaFile.type?.startsWith("video")) return "video";
  if (mediaFile.category === "image" || mediaFile.type?.startsWith("image")) return "image";
  return "unknown";
};

// ─────────────────────────────────────────────────────────────────────────────
// Authenticated media fetch (our backend route needs an auth header)
// ─────────────────────────────────────────────────────────────────────────────
const useAuthenticatedBlobUrl = (url, uid) => {
  const [blobUrl, setBlobUrl] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let objectUrl;
    let cancelled = false;
    setError(false);
    setBlobUrl(null);

    if (!url) return undefined;

    (async () => {
      try {
        const res = await fetch(url, {
          headers: uid ? { "X-User-UID": uid } : {},
        });
        if (!res.ok) throw new Error(`Failed to load media: ${res.status}`);
        const blob = await res.blob();
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setBlobUrl(objectUrl);
      } catch (e) {
        if (!cancelled) setError(true);
      }
    })();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [url, uid]);

  return { blobUrl, error };
};

// ─────────────────────────────────────────────────────────────────────────────
// Helper: resolve template type + all preview fields from a template object
// ─────────────────────────────────────────────────────────────────────────────
const resolveTemplateData = (template, templateRequests = []) => {
  if (!template?.components) {
    return {
      template_type: null,
      bodyText: "",
      title: "",
      mediaFile: null,
      mediaFallbackUrl: "",
      buttons: [],
      cards: [],
      footerText: "",
      isAuthentication: false,
    };
  }

  const comp = template.components;
  const bodyComp = comp.find((c) => c.type === "BODY");
  const headerComp = comp.find((c) => c.type === "HEADER");
  const buttonsComp = comp.find((c) => c.type === "BUTTONS");
  const carouselComp = comp.find((c) => c.type === "CAROUSEL");
  const footerComp = comp.find((c) => c.type === "FOOTER");

  const reqRecord = templateRequests.find(
    (r) => r.template_id === template.template_id
  );
  let template_type = reqRecord?.template_type ?? null;

  const isAuthentication =
    (template.category || "").toUpperCase() === "AUTHENTICATION";

  if (!template_type) {
    if (isAuthentication) template_type = "AUTHENTICATION";
    else if (carouselComp) template_type = "INTERACTIVE";
    else if (headerComp) template_type = "MEDIA";
    else template_type = "TEXT";
  }

  const headerHandle = safeFallback(headerComp?.example?.header_handle?.[0]);
  const headerMediaFile = template.media_files?.[0]
    ? {
        ...template.media_files[0],
        preview_url: resolvePreviewUrl(template.media_files[0].preview_url),
      }
    : null;

  const cardsWithMedia = (carouselComp?.cards ?? []).map((card, idx) => {
    const cardHeader = card.components?.find((c) => c.type === "HEADER");
    const cardHandle = safeFallback(cardHeader?.example?.header_handle?.[0]);
    const rawCardMediaFile = template.media_files?.[idx];
    const cardMediaFile = rawCardMediaFile
      ? { ...rawCardMediaFile, preview_url: resolvePreviewUrl(rawCardMediaFile.preview_url) }
      : null;
    return { ...card, _mediaFile: cardMediaFile, _mediaFallbackUrl: cardHandle };
  });

  return {
    name: template.name,
    template_type,
    bodyText: bodyComp?.text ?? "",
    title: headerComp?.text ?? "",
    mediaFile: headerMediaFile,
    mediaFallbackUrl: headerHandle,
    buttons: buttonsComp?.buttons ?? [],
    cards: cardsWithMedia,
    footerText: footerComp?.text ?? "",
    isAuthentication,
  };
};

// ─────────────────────────────────────────────────────────────────────────────
// Template parameter counting ({{1}} … {{N}})
// WhatsApp numbers placeholders separately per component, so we add them up:
//   HEADER (text) + BODY + URL buttons  (+ the same for every carousel card)
// The CSV then needs param1 … paramN columns.
// ─────────────────────────────────────────────────────────────────────────────

// Highest {{N}} found in a piece of text (0 if none)
const maxPlaceholder = (text) => {
  const nums = (String(text || "").match(/{{\s*(\d+)\s*}}/g) || []).map((m) =>
    parseInt(m.match(/\d+/)[0], 10)
  );
  return nums.length ? Math.max(...nums) : 0;
};

// Count of {{N}} for ONE component list (header + body + URL buttons)
const countComponentVars = (components = []) => {
  let total = 0;
  components.forEach((c) => {
    if (c.type === "HEADER" && c.format === "TEXT") {
      total += maxPlaceholder(c.text);
    } else if (c.type === "BODY") {
      total += maxPlaceholder(c.text);
    } else if (c.type === "BUTTONS") {
      (c.buttons || []).forEach((b) => {
        if (b.type === "URL") total += maxPlaceholder(b.url);
      });
    }
  });
  return total;
};

// Total params needed across the FULL template (incl. carousel cards)
const getVariableCount = (template) => {
  if (!template?.components) return 0;

  let total = countComponentVars(template.components);

  const carousel = template.components.find((c) => c.type === "CAROUSEL");
  (carousel?.cards || []).forEach((card) => {
    total += countComponentVars(card.components);
  });

  return total;
};

// ─────────────────────────────────────────────────────────────────────────────
// Image thumbnail with automatic fallback
// ─────────────────────────────────────────────────────────────────────────────
const TemplateMediaThumb = ({ mediaFile, fallbackSrc, alt, height, uid }) => {
  const authUrl = mediaFile?.preview_url || "";
  const { blobUrl, error } = useAuthenticatedBlobUrl(authUrl, uid);
  const [usingFallback, setUsingFallback] = useState(false);

  useEffect(() => {
    setUsingFallback(false);
  }, [authUrl, fallbackSrc]);

  useEffect(() => {
    if (error && fallbackSrc) setUsingFallback(true);
  }, [error, fallbackSrc]);

  const kind = mediaFileKind(mediaFile);
  const src = usingFallback ? fallbackSrc : blobUrl;
  const stillLoading = !!authUrl && !blobUrl && !error && !usingFallback;

  if (!src) {
    return (
      <Box
        sx={{
          height,
          backgroundColor: "#E2E8F0",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {stillLoading ? (
          <CircularProgress size={16} />
        ) : (
          <Typography variant="caption" color="text.secondary" fontSize={height <= 55 ? "9px" : undefined}>
            Image
          </Typography>
        )}
      </Box>
    );
  }

  if (kind === "video" && !usingFallback) {
    return (
      <Box sx={{ height, overflow: "hidden", backgroundColor: "#000" }}>
        <video
          src={src}
          muted
          playsInline
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
          onError={() => {
            if (!usingFallback && fallbackSrc) setUsingFallback(true);
          }}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ height, overflow: "hidden", backgroundColor: "#f1f5f9" }}>
      <img
        src={src}
        alt={alt}
        style={{ width: "100%", height: "100%", objectFit: "cover" }}
        onError={() => {
          if (!usingFallback && fallbackSrc && fallbackSrc !== src) {
            setUsingFallback(true);
          }
        }}
      />
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
const WhatsappComponent = ({ service_id }) => {
  const navigate = useNavigate();

  const [campaignName, setCampaignName] = useState("");
  const [campaignCategory, setCampaignCategory] = useState(10);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [dateTime, setDateTime] = useState("");
  const [timeZone, setTimeZone] = useState("Asia/Kolkata");
  const [enableDateTime, setEnableDateTime] = useState(false);
  const [filterDatatill_days, setFilterDatatill_days] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [currentTime, setCurrentTime] = useState("");
  const [selectedDbFile, setSelectedDbFile] = useState(null);
  const [isFileModalOpen, setIsFileModalOpen] = useState(false);
  const [customerId, setCustomerId] = useState(null);
  const [batch_size, setBatchSize] = useState("");
  const [sendInterval, setSendInterval] = useState("");
  const [selectedPhno, setSelectedPhno] = useState("");
  const [selectedWabaId, setSelectedWabaId] = useState("");
  const [enableFilterTill, setEnableFilterTill] = useState(false);
  const [enableBatching, setEnableBatching] = useState(false);
  const [testMobileNumber, setTestMobileNumber] = useState([""]);
  const [templateSearch, setTemplateSearch] = useState("");
  const [testOtpCode, setTestOtpCode] = useState("");
  const [otpColumnName, setOtpColumnName] = useState("otp");
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);

  // NEW: response of the preview API. Submit is only possible when this is
  // set AND ready_to_send is true.
  const [previewData, setPreviewData] = useState(null);

  const { data: dbFiles, isLoading: dbFilesLoading } = useGetSearchFilesQuery();
  const { data: phoneData } = useGetPhoneNUmberQuery(customerId, {
    skip: !customerId,
  });

  const { data: checkmpin } = useCheckMpinStatusQuery();

  const {
    data: templateData = {},
    isLoading,
    isError,
  } = useGetTemplatesQuery(
    { customer_id: customerId },
    { skip: !customerId || !selectedWabaId }
  );

  const [sendMessage, { isLoading: sendMessageLoading }] =
    useSendMessageBatchMutation();
  const [sendTestMessage, { isLoading: isTestSending }] =
    useTestwhatsappmsgMutation();
  const [previewCampaign, { isLoading: isPreviewing }] =
    useGetwhatsappcampaignpreviewMutation();

  const templateRequests = templateData?.template_requests ?? [];

  const templates = (templateData?.templates ?? [])
    .filter((t) => String(t.waba_id) === String(selectedWabaId))
    .filter((t) => t.status?.toUpperCase() === "APPROVED")
    .sort((a, b) => a.name.localeCompare(b.name));

  const filteredTemplates = templates.filter((t) =>
    t.name?.toLowerCase().includes(templateSearch.trim().toLowerCase())
  );

  const selectedTemplate =
    templates.find((t) => t.template_id === selectedId) ?? null;
  const templatePreviewData = selectedTemplate
    ? resolveTemplateData(selectedTemplate, templateRequests)
    : null;

  const isAuthTemplate = templatePreviewData?.isAuthentication ?? false;

  const selectedWabaLabel =
    phoneData?.phone_numbers?.find((p) => p.phone_number_id === selectedPhno)
      ?.phone_number || "Not selected";

  const campaignCategoryLabel =
    CAMPAIGN_CATEGORIES.find((c) => c.value === campaignCategory)?.label ||
    "—";

  // Clock
  useEffect(() => {
    const tick = () => {
      const now = new Date();
      setCurrentTime(
        `${now.getHours().toString().padStart(2, "0")}:${now
          .getMinutes()
          .toString()
          .padStart(2, "0")}`
      );
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  // Decode JWT → customerId
  useEffect(() => {
    try {
      const token = localStorage.getItem("accessToken");
      if (!token) {
        toast.error("Access token not found");
        return;
      }
      const decoded = jwtDecode(token);
      const id =
        decoded?.customer_id || decoded?.id || decoded?.userid || null;
      if (!id) toast.error("Customer ID not found in token");
      else setCustomerId(id);
    } catch (err) {
      toast.error("Invalid access token format");
    }
  }, []);

  // Auto-sync category dropdown for Auth templates
  useEffect(() => {
    if (isAuthTemplate) setCampaignCategory(30);
  }, [isAuthTemplate]);

  // Reset page when search changes
  useEffect(() => {
    setPage(0);
  }, [templateSearch]);

  // Handlers
  const handleChangePage = (_, p) => setPage(p);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const validateCampaign = () => {
    if (!campaignName.trim()) {
      toast.error("Campaign name is required");
      return false;
    }
    if (!selectedId) {
      toast.error("Please select a template");
      return false;
    }
    if (!selectedFile && !selectedDbFile) {
      toast.error("Please upload a file or select from database");
      return false;
    }
    if (!selectedPhno) {
      toast.error("Please select a WABA number");
      return false;
    }
    if (isAuthTemplate && !otpColumnName.trim()) {
      toast.error("Please specify the OTP column name from your file");
      return false;
    }
    return true;
  };

  const closeReviewModal = () => {
    setIsReviewModalOpen(false);
    setPreviewData(null);
  };

  // ── Preview: call the preview API, then open the review modal ────────────
  // Only the fields the user actually filled are appended.
  const handlePreviewClick = async () => {
    if (!validateCampaign()) return;

    setPreviewData(null); // never keep a stale preview around

    try {
      const formData = new FormData();
      formData.append("phone_number_id", selectedPhno);
      formData.append("template_id", selectedId);
      formData.append("service_id", service_id);
      formData.append("message_type", "template");
      formData.append("campaign_name", campaignName);
      formData.append("customer_id", customerId);

      if (selectedFile) formData.append("csv_file", selectedFile);
      if (selectedDbFile) formData.append("file_path", selectedDbFile.path);
      if (enableFilterTill && filterDatatill_days)
        formData.append("filter_days", filterDatatill_days);
      if (isAuthTemplate && otpColumnName.trim())
        formData.append("otp_column", otpColumnName.trim());

      const res = await previewCampaign({ customer_id: customerId, formData }).unwrap();
      setPreviewData(res);
      setIsReviewModalOpen(true);
    } catch (err) {
      toast.error(
        err?.data?.error?.error_user_msg ||
          err?.data?.error?.message ||
          err?.data?.message ||
          err?.error ||
          "Failed to load preview"
      );
    }
  };

  // ── Final submit (only reachable from inside the preview modal) ──────────
  const handleSubmit = async () => {
    if (!validateCampaign()) return;
    if (!previewData?.ready_to_send) {
      toast.error("Campaign is not ready to send");
      return;
    }

    try {
      const formData = new FormData();
      formData.append("campaign_name", campaignName);
      formData.append("campaign_category", campaignCategory);
      formData.append("customer_id", customerId);
      formData.append("service_id", service_id);
      formData.append("template_id", selectedId);
      if (selectedFile) formData.append("csv_file", selectedFile);
      if (selectedDbFile) formData.append("file_path", selectedDbFile.path);
      if (enableDateTime && dateTime) {
        formData.append("message_send_start_time", dateTime);
        formData.append("time_zone", timeZone);
      }
      if (enableBatching) {
        formData.append("batch_size", batch_size);
        formData.append("message_send_interval", sendInterval);
      }

      formData.append("phone_number_id", selectedPhno || "");
      formData.append("waba_id", selectedWabaId || "");

      if (enableFilterTill && filterDatatill_days)
        formData.append("filter_days", filterDatatill_days);

      if (isAuthTemplate) {
        formData.append("otp_column", otpColumnName.trim());
      }

      await sendMessage({ formData, customer_id: customerId }).unwrap();
      toast.success("Message sent successfully");
      setSelectedId(null);
      setSelectedFile(null);
      setSelectedDbFile(null);
      setDateTime("");
      setBatchSize("");
      setSendInterval("");
      setCampaignName("");
      closeReviewModal();
    } catch (err) {
      toast.error(
        err?.data?.error?.error_user_msg ||
          err?.data?.error?.message ||
          err?.data?.message ||
          err?.error ||
          "Error submitting message"
      );
    }
  };

  const handleConfirmSubmit = async () => {
    if (isConfirming) return;
    setIsConfirming(true);
    try {
      await handleSubmit();
    } finally {
      setIsConfirming(false);
    }
  };

  const handleTestSend = async () => {
    if (!selectedId) {
      toast.error("Please select a template first");
      return;
    }
    if (!selectedPhno) {
      toast.error("Please select a WABA number");
      return;
    }
    if (
      !testMobileNumber ||
      testMobileNumber.length === 0 ||
      !testMobileNumber[0]
    ) {
      toast.error("Please enter a mobile number for testing");
      return;
    }
    if (isAuthTemplate && !testOtpCode.trim()) {
      toast.error("Please enter an OTP code to test with");
      return;
    }

    try {
      const formData = new FormData();
      formData.append("campaign_name", "Test Send");
      formData.append("phone_number_id", selectedPhno);
      formData.append("recipients", testMobileNumber.join(","));
      formData.append("message_type", "template");
      formData.append("template_id", selectedId);

      const otp = testOtpCode.trim();
      const templateComponents = isAuthTemplate
        ? [
            {
              type: "body",
              parameters: [{ type: "text", text: otp }],
            },
            {
              type: "button",
              sub_type: "url",
              index: "0",
              parameters: [{ type: "text", text: otp }],
            },
          ]
        : [];

      formData.append("template_components", JSON.stringify(templateComponents));

      await sendTestMessage({
        formData,
        customer_id: customerId,
      }).unwrap();
      toast.success("Test message sent successfully");
    } catch (error) {
      toast.error(
        error?.data?.error?.error_user_msg ||
          error?.data?.error?.message ||
          error?.data?.message ||
          error?.error ||
          "Failed to send test message"
      );
    }
  };

  const handleDownloadSample = () => {
    if (!selectedTemplate) {
      toast.error("Please select a template first");
      return;
    }

    if (isAuthTemplate) {
      const ws = XLSX.utils.aoa_to_sheet([["phone_number", otpColumnName || "otp"]]);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Sample");
      XLSX.writeFile(wb, `${selectedTemplate.name}_sample.csv`);
      return;
    }

    // Body / header / URL-button / carousel-card {{N}} placeholders → param1…paramN
    const varCount = getVariableCount(selectedTemplate);
    const headers = [
      "phone_number",
      ...Array.from({ length: varCount }, (_, i) => `param${i + 1}`),
    ];
    const ws = XLSX.utils.aoa_to_sheet([headers]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sample");
    XLSX.writeFile(wb, `${selectedTemplate.name}_sample.csv`);
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      setSelectedFile(null);
      return;
    }
    setSelectedDbFile(null);
    setSelectedFile(file);
  };

  const toggleDrawer = (open) => () => setOpenDrawer(open);

  const timeZones = [
    "UTC",
    "America/New_York",
    "Europe/London",
    "Asia/Kolkata",
    "Asia/Tokyo",
    "Asia/Calcutta",
  ];
  const tzOffset = new Date().getTimezoneOffset() * 60000;
  const minDateTime = new Date(Date.now() - tzOffset).toISOString().slice(0, 16);

  // ── Template preview renderer ─────────────────────────────────────────────
  const renderTemplatePreview = () => {
    if (!selectedTemplate || !templatePreviewData) {
      return (
        <Box sx={{ textAlign: "center", p: 2, color: "text.secondary" }}>
          <Typography variant="caption" display="block">
            Select a template
          </Typography>
          <Typography variant="caption" display="block">
            to preview
          </Typography>
        </Box>
      );
    }

    const {
      template_type,
      bodyText,
      title,
      mediaFile,
      mediaFallbackUrl,
      buttons,
      cards,
      footerText,
    } = templatePreviewData;

    if (template_type === "AUTHENTICATION") {
      const previewOtp = testOtpCode || "123456";
      return (
        <Box sx={{ alignSelf: "flex-end", maxWidth: "88%", ml: "auto", mb: 1 }}>
          <Box
            sx={{
              p: "8px 10px",
              backgroundColor: "#DCF8C6",
              borderRadius: "10px 10px 0 10px",
              fontSize: "11px",
              lineHeight: 1.5,
              wordBreak: "break-word",
            }}
          >
            {(bodyText || "*{{1}}* is your verification code.").replace(
              "{{1}}",
              previewOtp
            )}
          </Box>
          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mt: 0.5 }}>
            <Button
              variant="outlined"
              size="small"
              sx={{
                fontSize: "9px",
                p: "2px 6px",
                minWidth: "auto",
                borderRadius: "8px",
              }}
            >
              Copy code
            </Button>
          </Box>
        </Box>
      );
    }

    if (template_type === "TEXT") {
      return (
        <Box sx={{ alignSelf: "flex-end", maxWidth: "88%", ml: "auto", mb: 1 }}>
          <Box
            sx={{
              p: "8px 10px",
              backgroundColor: "#DCF8C6",
              borderRadius: "10px 10px 0 10px",
              fontSize: "11px",
              lineHeight: 1.5,
              wordBreak: "break-word",
            }}
          >
            {bodyText || <em style={{ color: "#999" }}>No body text</em>}
            {footerText && (
              <Box
                sx={{
                  mt: 0.5,
                  fontSize: "9px",
                  color: "#888",
                  borderTop: "1px solid #b2d8a8",
                  pt: 0.5,
                }}
              >
                {footerText}
              </Box>
            )}
          </Box>
          {buttons.length > 0 && (
            <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mt: 0.5 }}>
              {buttons.map((btn, i) => (
                <Button
                  key={i}
                  variant="outlined"
                  size="small"
                  sx={{
                    fontSize: "9px",
                    p: "2px 6px",
                    minWidth: "auto",
                    borderRadius: "4px",
                  }}
                >
                  {btn.text}
                </Button>
              ))}
            </Box>
          )}
        </Box>
      );
    }

    if (template_type === "MEDIA") {
      return (
        <Box sx={{ maxWidth: "92%", mb: 1 }}>
          <Card
            sx={{
              borderRadius: "10px",
              overflow: "hidden",
              border: "1px solid #E2E8F0",
            }}
          >
            <TemplateMediaThumb
              mediaFile={mediaFile}
              fallbackSrc={mediaFallbackUrl}
              alt="Preview"
              height={90}
              uid={customerId}
            />
            <Box sx={{ p: "8px 10px", backgroundColor: "#DCF8C6" }}>
              {title && (
                <Typography
                  sx={{
                    fontSize: "11px",
                    fontWeight: 700,
                    mb: 0.5,
                    color: "#1a1a1a",
                  }}
                >
                  {title}
                </Typography>
              )}
              <Typography
                sx={{
                  fontSize: "11px",
                  lineHeight: 1.5,
                  color: "#1a1a1a",
                  wordBreak: "break-word",
                }}
              >
                {bodyText || <em style={{ color: "#999" }}>No body text</em>}
              </Typography>
              {footerText && (
                <Typography
                  sx={{
                    mt: 0.5,
                    fontSize: "9px",
                    color: "#888",
                    borderTop: "1px solid #b2d8a8",
                    pt: 0.5,
                  }}
                >
                  {footerText}
                </Typography>
              )}
              {buttons.length > 0 && (
                <Box
                  sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mt: 0.5 }}
                >
                  {buttons.map((btn, i) => (
                    <Button
                      key={i}
                      variant="outlined"
                      size="small"
                      sx={{
                        fontSize: "9px",
                        p: "2px 6px",
                        minWidth: "auto",
                        borderRadius: "4px",
                      }}
                    >
                      {btn.text}
                    </Button>
                  ))}
                </Box>
              )}
            </Box>
          </Card>
        </Box>
      );
    }

    if (template_type === "INTERACTIVE") {
      return (
        <Box sx={{ maxWidth: "100%", mb: 1 }}>
          {bodyText && (
            <Box
              sx={{
                mx: 0.5,
                mb: 0.75,
                p: "6px 8px",
                backgroundColor: "#DCF8C6",
                borderRadius: "8px",
                fontSize: "11px",
                lineHeight: 1.4,
                wordBreak: "break-word",
              }}
            >
              {bodyText}
            </Box>
          )}
          <Box
            sx={{
              display: "flex",
              gap: 1,
              overflowX: "auto",
              pb: 0.5,
              px: 0.5,
            }}
          >
            {cards.map((card, idx) => {
              const cardBody = card.components?.find((c) => c.type === "BODY");
              const cardButtons = card.components?.find(
                (c) => c.type === "BUTTONS"
              );
              return (
                <Card
                  key={idx}
                  sx={{
                    minWidth: "110px",
                    maxWidth: "130px",
                    flexShrink: 0,
                    borderRadius: "8px",
                    overflow: "hidden",
                    border: "1px solid #E2E8F0",
                  }}
                >
                  <TemplateMediaThumb
                    mediaFile={card._mediaFile}
                    fallbackSrc={card._mediaFallbackUrl}
                    alt={`Card ${idx + 1}`}
                    height={60}
                    uid={customerId}
                  />
                  <Box sx={{ p: "6px 8px", backgroundColor: "#fff" }}>
                    <Typography
                      sx={{
                        fontSize: "10px",
                        fontWeight: 600,
                        color: "#1a1a1a",
                        mb: 0.3,
                        lineHeight: 1.3,
                      }}
                    >
                      {cardBody?.text || `Card ${idx + 1}`}
                    </Typography>
                    {cardButtons?.buttons?.map((btn, bi) => (
                      <Button
                        key={bi}
                        variant="outlined"
                        fullWidth
                        size="small"
                        sx={{
                          fontSize: "8px",
                          p: "1px 4px",
                          mt: 0.3,
                          minWidth: "auto",
                          borderRadius: "3px",
                          textTransform: "none",
                        }}
                      >
                        {btn.text}
                      </Button>
                    ))}
                  </Box>
                </Card>
              );
            })}
          </Box>
        </Box>
      );
    }

    return (
      <Box sx={{ textAlign: "center", p: 1 }}>
        <Typography
          variant="body2"
          fontWeight={500}
          color="primary"
          fontSize="11px"
        >
          {selectedTemplate.name}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {template_type}
        </Typography>
      </Box>
    );
  };

  // ── Reusable phone mockup ────────────────────────────────────────────────
  const renderPhoneMockup = () => (
    <Box>
      <PhonePreview>
        <Box
          sx={{
            height: 22,
            px: 1,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            background: "#000",
            color: "#fff",
          }}
        >
          <Typography fontSize="9px">{currentTime}</Typography>
          <Box sx={{ display: "flex", gap: 0.5 }}>
            <WifiIcon sx={{ fontSize: 10 }} />
            <NetworkCellIcon sx={{ fontSize: 10 }} />
            <BatteryFullIcon sx={{ fontSize: 10 }} />
          </Box>
        </Box>

        <Box
          sx={{
            height: 44,
            px: 1,
            display: "flex",
            alignItems: "center",
            borderBottom: "1px solid #E2E8F0",
            background: "#fff",
          }}
        >
          <IconButton size="small" sx={{ p: 0.5, mr: 0.5 }}>
            <ArrowBackIcon sx={{ fontSize: 16 }} />
          </IconButton>
          <Typography variant="caption" sx={{ fontWeight: 600, fontSize: "11px" }}>
            Business Message
          </Typography>
        </Box>

        <Box
          sx={{
            flex: 1,
            p: 1,
            background: "#E5DDD5",
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {renderTemplatePreview()}
        </Box>
      </PhonePreview>

      {templatePreviewData && (
        <Box sx={{ textAlign: "center", mt: 1 }}>
          <Typography
            variant="caption"
            sx={{
              display: "inline-block",
              px: 1.5,
              py: 0.3,
              borderRadius: "12px",
              fontSize: "10px",
              fontWeight: 600,
              background: "#E0F2FE",
              color: "#0277BD",
            }}
          >
            {templatePreviewData.template_type}
          </Typography>
        </Box>
      )}
    </Box>
  );

  // ── Review modal data ─────────────────────────────────────────────────────
  // Local-only settings (not part of the preview API response)
  const localFields = [
    { label: "Waba Number", value: selectedWabaLabel },
    {
      label: "File Source",
      value: selectedFile
        ? selectedFile.name
        : selectedDbFile
          ? `From DB: ${selectedDbFile.filter_name}`
          : "No file selected",
    },
    ...(isAuthTemplate
      ? [{ label: "OTP Column", value: otpColumnName || "—" }]
      : []),
    {
      label: "Batching",
      value: enableBatching
        ? `${batch_size || "—"} msgs every ${sendInterval || "—"} min`
        : "Disabled",
    },
    {
      label: "Schedule",
      value:
        enableDateTime && dateTime
          ? `${dateTime} (${timeZone})`
          : "Immediately",
    },
  ];

  const pr = previewData?.processing_results;
  const cd = previewData?.campaign_details;
  const bal = previewData?.checks?.balance;
  const tpl = previewData?.checks?.template;

  // Template ID and post_via are intentionally NOT shown.
  const serverFields = previewData
    ? [
        { label: "Campaign Name", value: cd?.campaign_name },
        { label: "Category", value: campaignCategoryLabel },
        { label: "Message Type", value: cd?.message_type },
        { label: "Language", value: cd?.language },
        { label: "Template Name", value: cd?.template_name },
        { label: "Filter Days", value: cd?.filter_days },
      ]
    : [];

  const processingFields = pr
    ? [
        { label: "Total Posted", value: pr.total_posted },
        { label: "Total Remains", value: pr.total_remains },
        { label: "Last Campaign Filter", value: pr.last_campaign_filter },
        { label: "Customer Blacklist", value: pr.customer_blacklist },
        { label: "Duplicates", value: pr.duplicates },
      ]
    : [];

  // ── Drawer content ────────────────────────────────────────────────────────
  const DrawerList = (
    <Box
      sx={{
        p: 1.5,
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1.5,
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 600, fontSize: "0.85rem" }}
        >
          Select Template
        </Typography>
        <IconButton
          onClick={() => {
            setOpenDrawer(false);
            setTemplateSearch("");
          }}
          size="small"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <TextField
        label="Search..."
        type="search"
        variant="outlined"
        size="small"
        fullWidth
        value={templateSearch}
        onChange={(e) => setTemplateSearch(e.target.value)}
        sx={{ mb: 1.5, ...compactField, ...inputSx }}
      />

      <TableContainer
        component={Paper}
        sx={{
          flex: 1,
          borderRadius: "8px",
          border: "1px solid #E2E8F0",
          "& .MuiTableCell-root": { fontSize: "0.75rem", py: 0.5 },
        }}
      >
        <Table size="small">
          <TableHead sx={{ backgroundColor: "#F8FAFC" }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, width: "60px" }}>
                Select
              </TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Template Name</TableCell>
              <TableCell sx={{ fontWeight: 600, width: "110px" }}>
                Template Type
              </TableCell>
              <TableCell sx={{ fontWeight: 600, width: "100px" }}>
                Template Category
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                  <CircularProgress size={20} />
                </TableCell>
              </TableRow>
            ) : isError ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Typography variant="caption" color="error">
                    Error loading
                  </Typography>
                </TableCell>
              </TableRow>
            ) : filteredTemplates.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Typography variant="caption" color="text.secondary">
                    {templateSearch
                      ? "No templates match your search"
                      : "No templates"}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredTemplates
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((t) => {
                  const resolved = resolveTemplateData(t, templateRequests);
                  return (
                    <TableRow
                      key={t.template_id}
                      hover
                      sx={{
                        cursor: "pointer",
                        backgroundColor:
                          selectedId === t.template_id
                            ? "#E0F2FE"
                            : "inherit",
                        "&:hover": { backgroundColor: "#F1F5F9" },
                      }}
                      onClick={() => setSelectedId(t.template_id)}
                    >
                      <TableCell>
                        <Radio
                          size="small"
                          checked={selectedId === t.template_id}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" fontWeight={500}>
                          {t.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {getTemplateTypeLabel(resolved.template_type)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {t.category}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mt: 1.5,
        }}
      >
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredTemplates.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          size="small"
          sx={{
            "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
              { fontSize: "0.7rem" },
          }}
        />
      </Box>
    </Box>
  );

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: 1.5, minHeight: "100vh" }}>
      <Grid container spacing={1}>
        {/* ── Left Column ── */}
        <Grid size={8}>
          <Card
            sx={{
              p: 1.5,
              borderRadius: "12px",
              border: "1px solid #E2E8F0",
              boxShadow: "0 2px 6px rgba(0,0,0,0.04)",
              mb: 1.5,
            }}
          >
            {/* Campaign Details */}
            <Box sx={{ mb: 1.5 }}>
              <SectionTitle title="Campaign Details" />
              <Box
                sx={{
                  display: "flex",
                  gap: 1.5,
                  flexWrap: "wrap",
                  alignItems: "center",
                }}
              >
                <TextField
                  label="Campaign Name"
                  value={campaignName}
                  onChange={(e) => setCampaignName(e.target.value)}
                  size="small"
                  sx={{ width: 240, ...compactField, ...inputSx }}
                />

                <FormControl
                  size="small"
                  sx={{ width: 240, ...compactField, ...selectSx }}
                >
                  <InputLabel>Waba Number</InputLabel>
                  <Select
                    label="Waba Number"
                    value={selectedPhno}
                    onChange={(e) => {
                      const chosenPhoneNumberId = e.target.value;
                      setSelectedPhno(chosenPhoneNumberId);
                      const matched = phoneData?.phone_numbers?.find(
                        (p) => p.phone_number_id === chosenPhoneNumberId
                      );
                      setSelectedWabaId(matched?.waba_id || "");
                    }}
                  >
                    {phoneData?.phone_numbers?.map((p) => (
                      <MenuItem key={p.id} value={p.phone_number_id}>
                        {p.phone_number}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Box>

            {/* Template Selection */}
            <Box sx={{ mb: 1.5 }}>
              <SectionTitle title="Select Template" />
              {!selectedTemplate ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <TextField
                    placeholder="No template selected"
                    size="small"
                    disabled
                    sx={{
                      width: 320,
                      ...compactField,
                      ...inputSx,
                      "& .MuiOutlinedInput-input.Mui-disabled": {
                        WebkitTextFillColor: "#000 !important",
                      },
                      "& input::placeholder": {
                        color: "#000 !important",
                        opacity: "1 !important",
                      },
                    }}
                  />
                  <Button
                    variant="contained"
                    size="small"
                    onClick={toggleDrawer(true)}
                    disabled={!customerId}
                    sx={{
                      fontSize: "0.72rem",
                      textTransform: "none",
                      borderRadius: "6px",
                      px: 1.2,
                      py: 0.6,
                    }}
                  >
                    Browse
                  </Button>
                </Box>
              ) : (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      flex: 1,
                    }}
                  >
                    <DoneIcon sx={{ fontSize: 14, color: "#4caf50" }} />
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 500, fontSize: "0.8rem" }}
                    >
                      {selectedTemplate.name}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ ml: 0.5 }}
                    >
                      ({templatePreviewData?.template_type})
                    </Typography>
                  </Box>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleDownloadSample}
                    sx={{
                      fontSize: "0.72rem",
                      textTransform: "none",
                      px: 1.2,
                      py: 0.6,
                    }}
                  >
                    Download Sample
                  </Button>
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => setSelectedId(null)}
                    sx={{ minWidth: "auto", p: 0.5 }}
                  >
                    <CloseIcon fontSize="small" />
                  </Button>
                </Box>
              )}
            </Box>

            {/* Upload & Configure */}
            <Box sx={{ mb: 1.5 }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <SectionTitle title="Upload & Configure" />
              </Box>

              {/* Row 1: Upload + Database + OTP column */}
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  flexWrap: "wrap",
                  alignItems: "center",
                }}
              >
                <Box sx={{ display: "flex", gap: 1, width: 220 }}>
                  <Button
                    component="label"
                    variant="outlined"
                    startIcon={<CloudUploadIcon />}
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      height: 35,
                      px: 1,
                      textTransform: "none",
                      fontSize: "0.75rem",
                      whiteSpace: "nowrap",
                      background: "rgba(255,255,255,0.7)",
                      backdropFilter: "blur(8px)",
                      borderRadius: "8px",
                      "& .MuiButton-startIcon": { mr: 0.5 },
                      "&:hover": {
                        background: "rgba(255,255,255,0.9)",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                      },
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        display: "block",
                      }}
                    >
                      {selectedFile ? selectedFile.name : "Upload"}
                    </Box>
                    <VisuallyHiddenInput
                      type="file"
                      accept=".csv,.txt,.xlsx"
                      onChange={handleFileChange}
                    />
                  </Button>

                  <Button
                    variant="outlined"
                    startIcon={<StorageIcon />}
                    onClick={() => setIsFileModalOpen(true)}
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      height: 35,
                      px: 1,
                      textTransform: "none",
                      fontSize: "0.75rem",
                      whiteSpace: "nowrap",
                      background: "rgba(255,255,255,0.7)",
                      backdropFilter: "blur(8px)",
                      borderRadius: "8px",
                      "& .MuiButton-startIcon": { mr: 0.5 },
                      "&:hover": {
                        background: "rgba(255,255,255,0.9)",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                      },
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        display: "block",
                      }}
                    >
                      {selectedDbFile ? selectedDbFile.filter_name : "Database"}
                    </Box>
                  </Button>
                </Box>

                {isAuthTemplate && (
                  <TextField
                    label="OTP Column in File"
                    size="small"
                    value={otpColumnName}
                    onChange={(e) => setOtpColumnName(e.target.value)}
                    sx={{ width: 200, ...compactField, ...inputSx }}
                  />
                )}
              </Box>

              {/* Row 2: Enable Split + Batch Size + Interval + Filter From LC */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 1.5,
                  mt: 1.25,
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={enableBatching}
                      onChange={(e) => {
                        setEnableBatching(e.target.checked);
                        if (!e.target.checked) {
                          setBatchSize("");
                          setSendInterval("");
                        }
                      }}
                    />
                  }
                  label="Enable Split"
                  sx={{
                    m: 0,
                    "& .MuiFormControlLabel-label": {
                      fontSize: "0.7rem",
                      fontWeight: 500,
                    },
                  }}
                />

                {enableBatching && (
                  <>
                    <TextField
                      label="Batch Size"
                      type="number"
                      size="small"
                      value={batch_size}
                      onChange={(e) => setBatchSize(e.target.value)}
                      sx={{ width: 130, ...compactField, ...inputSx }}
                    />
                    <TextField
                      label="Interval (min)"
                      type="number"
                      size="small"
                      value={sendInterval}
                      onChange={(e) => setSendInterval(e.target.value)}
                      sx={{ width: 130, ...compactField, ...inputSx }}
                    />
                  </>
                )}

                {checkmpin?.data?.allow7_day_filter && (
                  <>
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={enableFilterTill}
                          onChange={(e) => {
                            setEnableFilterTill(e.target.checked);
                            if (!e.target.checked) setFilterDatatill_days(0);
                          }}
                        />
                      }
                      label="Filter From LC"
                      sx={{
                        m: 0,
                        "& .MuiFormControlLabel-label": {
                          fontSize: "0.7rem",
                        },
                      }}
                    />

                    {enableFilterTill && (
                      <FormControl
                        size="small"
                        sx={{ width: 150, ...compactField, ...selectSx }}
                      >
                        <InputLabel sx={{ fontSize: "0.7rem" }}>
                          Filter Till
                        </InputLabel>
                        <Select
                          value={filterDatatill_days}
                          label="Filter Till"
                          onChange={(e) => setFilterDatatill_days(e.target.value)}
                        >
                          <MenuItem value={1}>Today</MenuItem>
                          <MenuItem value={2}>Yesterday</MenuItem>
                          {Array.from({ length: 28 }, (_, i) => (
                            <MenuItem key={i + 3} value={i + 3}>
                              Last {i + 3} days
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  </>
                )}
              </Box>
            </Box>

            {/* Test & Schedule */}
            <Grid container spacing={1}>
              <Grid size={6}>
                <Box
                  sx={{
                    p: 1,
                    borderRadius: "8px",
                    border: "1px solid #E2E8F0",
                    background: "rgba(255,255,255,0.7)",
                  }}
                >
                  <SectionTitle title="Test" />
                  <TextField
                    label="Mobile Number"
                    size="small"
                    fullWidth
                    value={testMobileNumber.join(",")}
                    onChange={(e) =>
                      setTestMobileNumber(
                        e.target.value.split(",").map((num) => num.trim())
                      )
                    }
                    sx={{ mb: 1, ...compactField, ...inputSx }}
                  />
                  {isAuthTemplate && (
                    <TextField
                      label="Test OTP"
                      placeholder="e.g. 482193"
                      size="small"
                      fullWidth
                      value={testOtpCode}
                      onChange={(e) => setTestOtpCode(e.target.value)}
                      sx={{ mb: 1, ...compactField, ...inputSx }}
                    />
                  )}
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleTestSend}
                    disabled={isTestSending}
                    sx={{
                      fontSize: "0.72rem",
                      textTransform: "none",
                      px: 1.2,
                      py: 0.6,
                    }}
                  >
                    {isTestSending ? "Sending..." : "Test"}
                  </Button>
                </Box>
              </Grid>
              <Grid size={6}>
                <Box
                  sx={{
                    p: 1,
                    borderRadius: "8px",
                    border: "1px solid #E2E8F0",
                    background: "rgba(255,255,255,0.7)",
                  }}
                >
                  <SectionTitle title="Schedule" />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={enableDateTime}
                        onChange={(e) => setEnableDateTime(e.target.checked)}
                        size="small"
                      />
                    }
                    label={
                      <Typography variant="caption">Schedule later</Typography>
                    }
                    sx={{ mb: 0.5 }}
                  />
                  {enableDateTime && (
                    <Grid container spacing={1}>
                      <Grid size={7}>
                        <TextField
                          label="Date & Time"
                          type="datetime-local"
                          value={dateTime}
                          onChange={(e) => setDateTime(e.target.value)}
                          size="small"
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                          inputProps={{ min: minDateTime }}
                          sx={{ ...compactField, ...inputSx }}
                        />
                      </Grid>
                      <Grid size={5}>
                        <FormControl
                          fullWidth
                          size="small"
                          sx={{ ...compactField, ...selectSx }}
                        >
                          <InputLabel>Time Zone</InputLabel>
                          <Select
                            value={timeZone}
                            onChange={(e) => setTimeZone(e.target.value)}
                            label="Time Zone"
                          >
                            {timeZones.map((tz) => (
                              <MenuItem key={tz} value={tz}>
                                {tz.split("/").pop()}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Card>

          {/* Action Buttons — Submit now lives inside the preview modal */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 1,
              mt: 1,
              p: 1,
              borderTop: "1px solid #E2E8F0",
              background: "#fff",
              borderRadius: "8px",
            }}
          >
            <Button
              variant="contained"
              size="small"
              startIcon={
                isPreviewing ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <VisibilityIcon fontSize="small" />
                )
              }
              onClick={handlePreviewClick}
              disabled={isPreviewing}
              sx={{ minWidth: 100, fontSize: "0.72rem", textTransform: "none" }}
            >
              {isPreviewing ? "Checking..." : "Preview"}
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => navigate("/")}
              sx={{ minWidth: 80, fontSize: "0.72rem", textTransform: "none" }}
            >
              Cancel
            </Button>
          </Box>
        </Grid>

        {/* ── Right Column – Phone Preview ── */}
        <Grid size={4}>{renderPhoneMockup()}</Grid>
      </Grid>

      {/* ── Review / Preview Popup ── */}
      <Dialog
        open={isReviewModalOpen}
        onClose={closeReviewModal}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle sx={{ fontSize: "1rem", fontWeight: 600 }}>
          Review Your WhatsApp Campaign
        </DialogTitle>

        <DialogContent dividers>
          {!previewData ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {/* Ready status */}
              <Alert severity={previewData.ready_to_send ? "success" : "error"}>
                {previewData.ready_to_send
                  ? "Everything looks good. You can submit this campaign."
                  : previewData.ready_message || "Campaign is not ready to send"}
              </Alert>

              {/* Checks */}
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                <Chip
                  size="small"
                  color={tpl?.ok ? "success" : "error"}
                  label={`Template: ${
                    tpl?.template_status || (tpl?.ok ? "OK" : "Failed")
                  }`}
                />
                <Chip
                  size="small"
                  color={bal?.ok ? "success" : "error"}
                  label={`Balance: ${bal?.ok ? "Sufficient" : "Insufficient"}`}
                />
              </Box>

              <Box>
                <Typography
                  variant="subtitle1"
                  gutterBottom
                  sx={{ fontWeight: 600 }}
                >
                  Campaign Details
                </Typography>
                <FieldGrid fields={[...serverFields, ...localFields]} />
              </Box>

              <Divider />

              <Box>
                <Typography
                  variant="subtitle1"
                  gutterBottom
                  sx={{ fontWeight: 600 }}
                >
                  Contacts Summary
                </Typography>
                <FieldGrid fields={processingFields} />
              </Box>

              <Box>
                <Typography
                  variant="subtitle1"
                  gutterBottom
                  sx={{ fontWeight: 600 }}
                >
                  Balance
                </Typography>
                <FieldGrid
                  fields={[
                    {
                      label: "Available Balance",
                      value: `₹${bal?.available_balance}`,
                    },
                    {
                      label: "Estimated Cost",
                      value: `₹${bal?.estimated_total_cost}`,
                    },
                    { label: "Contacts", value: bal?.contacts_count },
                  ]}
                />
                {bal && !bal.ok && bal.reason && (
                  <Typography variant="caption" color="error">
                    {bal.reason}
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={closeReviewModal}>Edit</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleConfirmSubmit}
            disabled={
              !previewData ||
              !previewData.ready_to_send ||
              isConfirming ||
              sendMessageLoading
            }
          >
            {isConfirming || sendMessageLoading ? "Submitting..." : "Submit"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Database File Modal ── */}
      <Dialog
        open={isFileModalOpen}
        onClose={() => setIsFileModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 0.5, fontSize: "0.85rem", fontWeight: 600 }}>
          Database Files
        </DialogTitle>
        <DialogContent sx={{ pt: 0.5 }}>
          {dbFilesLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : dbFiles?.data?.length > 0 ? (
            <Box>
              <TableContainer sx={{ maxHeight: 300 }}>
                <Table
                  size="small"
                  sx={{
                    "& .MuiTableCell-root": { fontSize: "0.75rem", py: 0.5 },
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell>#</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell align="center">Count</TableCell>
                      <TableCell>Select</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dbFiles.data.map((file, idx) => (
                      <TableRow
                        key={file.path}
                        hover
                        sx={{
                          backgroundColor:
                            selectedDbFile?.path === file.path
                              ? "#E0F2FE"
                              : "inherit",
                        }}
                      >
                        <TableCell>{idx + 1}</TableCell>
                        <TableCell>{file.filter_name}</TableCell>
                        <TableCell align="center">{file.totalcount}</TableCell>
                        <TableCell>
                          <Radio
                            size="small"
                            checked={selectedDbFile?.path === file.path}
                            onChange={() => setSelectedDbFile(file)}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  mt: 1.5,
                  gap: 1,
                }}
              >
                <Button
                  onClick={() => setIsFileModalOpen(false)}
                  size="small"
                  sx={{ fontSize: "0.72rem" }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  disabled={!selectedDbFile}
                  onClick={() => {
                    if (selectedDbFile) {
                      setSelectedFile(null);
                      setIsFileModalOpen(false);
                    }
                  }}
                  sx={{ fontSize: "0.72rem" }}
                >
                  Select
                </Button>
              </Box>
            </Box>
          ) : (
            <Box sx={{ textAlign: "center", py: 3 }}>
              <Typography variant="body2" color="text.secondary">
                No database files
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Template Drawer ── */}
      <Drawer
        anchor="right"
        open={openDrawer}
        onClose={() => {
          setOpenDrawer(false);
          setTemplateSearch("");
        }}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: "600px", md: "55%", lg: "66%" },
            maxWidth: "590px",
            background: "#fff",
          },
        }}
      >
        {DrawerList}
      </Drawer>
    </Box>
  );
};

export default WhatsappComponent;